import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, BookOpen, ScrollText, Compass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  verse: { icon: BookOpen, label: "Verse Study" },
  dictionary: { icon: ScrollText, label: "Vine's Entry" },
  topic: { icon: Compass, label: "Nave's Topical" },
  word: { icon: BookOpen, label: "Word Study" },
  syntax: { icon: BookOpen, label: "Syntax" },
};

export default function Shared() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    axios.get(`${API}/share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Not found"))
      .finally(() => setBusy(false));
  }, [token]);

  if (busy) return <div className="text-center py-20"><Loader2 className="mx-auto animate-spin text-stone-400" /></div>;
  if (err) return (
    <div className="text-center py-20 space-y-4" data-testid="shared-error">
      <div className="font-serif-display text-3xl text-stone-800">This study is no longer shared.</div>
      <Link to="/" className="text-[#9A3B3B] underline underline-offset-4">Return home</Link>
    </div>
  );

  const meta = KIND_META[data.kind] || KIND_META.word;
  const Icon = meta.icon;
  const p = data.payload || {};

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="shared-page">
      <header className="border-b hairline pb-6">
        <div className="flex items-center gap-3 mb-3 text-xs uppercase tracking-[0.25em] text-[#78350F]">
          <Icon size={14} strokeWidth={1.5} /> Shared {meta.label}
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-stone-900 leading-tight">{data.title}</h1>
        <div className="mt-2 text-sm text-stone-500 italic">
          {data.author_name ? `Shared by ${data.author_name}` : "Anonymous reader"} &middot; {new Date(data.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </header>

      {data.kind === "verse" && (
        <div className="space-y-6">
          <blockquote className="font-serif-display text-2xl md:text-3xl italic leading-relaxed text-stone-800 border-l-2 border-[#9A3B3B] pl-6">
            "{p.text}"
          </blockquote>
          {p.interlinear?.length > 0 && (
            <div className="border hairline bg-[#FDFBF7] p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-4">Interlinear (KJV + Strong's)</div>
              <div className="flex flex-wrap gap-x-1 gap-y-4">
                {p.interlinear.map((tok, i) => (
                  <div key={i} className="ilw" style={{ cursor: "default" }}>
                    <span className="font-serif-display text-lg text-stone-900">{tok.text}</span>
                    {tok.strongs?.[0] && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-800 bg-amber-100/50 px-1">
                        {tok.strongs[0]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {data.kind === "dictionary" && (
        <article className="border hairline bg-[#FDFBF7] p-8 space-y-6">
          {p.original && (
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">{p.language} &middot; {p.part_of_speech}</div>
              <div className="font-serif-display text-3xl text-[#9A3B3B]">{p.original} <span className="italic text-stone-600 text-xl">{p.transliteration}</span></div>
            </div>
          )}
          {p.core_meaning && <p className="font-serif-display italic text-xl text-stone-800 leading-relaxed">{p.core_meaning}</p>}
          {p.senses?.map((s, i) => (
            <div key={i} className="border-l-2 border-[#9A3B3B]/30 pl-4">
              <div className="font-serif-display text-lg text-stone-900">{i + 1}. {s.label}</div>
              <div className="text-stone-700">{s.gloss}</div>
              {s.example_reference && <div className="text-xs font-mono text-stone-500 mt-1">{s.example_reference}</div>}
            </div>
          ))}
          {p.usage_notes && <p className="text-stone-700 leading-relaxed">{p.usage_notes}</p>}
        </article>
      )}

      {data.kind === "topic" && (
        <div className="space-y-6">
          {p.summary && (
            <p className="font-serif-display italic text-lg text-stone-800 leading-relaxed border-l-2 border-[#9A3B3B] pl-6">{p.summary}</p>
          )}
          {p.subtopics?.map((s, i) => (
            <div key={i} className="border-t hairline pt-4">
              <div className="font-serif-display text-2xl text-stone-900 mb-2">{s.title}</div>
              {s.description && <p className="text-sm text-stone-600 mb-2">{s.description}</p>}
              <ul className="space-y-1 font-mono text-sm text-[#9A3B3B]">
                {s.references?.map((r, j) => <li key={j}>{r}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="border-t hairline pt-6 flex items-center justify-between">
        <div className="text-sm text-stone-500 font-serif-display italic">"Study to shew thyself approved..."</div>
        <Link to="/" data-testid="shared-cta"
          className="text-sm bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-6 py-2 transition-colors flex items-center gap-2">
          Start your own study <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
}
